import React, { useEffect, useState } from 'react';
import { 
  Cog6ToothIcon,
  ArchiveBoxIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { API_ENDPOINTS } from '../constants/api';
import useNotification from '../hooks/useNotification';
import TelegramSettings from '../components/settings/TelegramSettings';

const Settings = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [activeTab, setActiveTab] = useState('archive'); // 'archive' или 'telegram'
  
  // Используем новый эндпоинт для глобальных настроек
  const { 
    data: projectSettings, 
    loading: isLoadingSettings, 
    fetchData: getProjectSettings, 
    putData: updateProjectSettings 
  } = useApi(API_ENDPOINTS.PROJECT_SETTINGS);

  // Загружаем глобальные настройки
  useEffect(() => {
    const loadProjectSettings = async () => {
      try {
        await getProjectSettings();
      } catch (error) {
        showError('Ошибка загрузки настроек проекта');
      }
    };
    if (user && user.roles.includes('ADMIN')) {
      loadProjectSettings();
    }
  }, [user, getProjectSettings, showError]);

  const handleAutoArchiveToggle = async () => {
    try {
      if (!projectSettings) {
        showError('Настройки проекта не загружены.');
        return;
      }

      const newAutoArchiveEnabled = !projectSettings.autoArchiveEnabled;
      await updateProjectSettings({ autoArchiveEnabled: newAutoArchiveEnabled });
      
      // Обновляем данные в useApi hook
      await getProjectSettings();
      
      showSuccess(newAutoArchiveEnabled ? 'Глобальная автоматическая архивация включена' : 'Глобальная автоматическая архивация отключена');
    } catch (error) {
      showError('Ошибка обновления настройки');
    }
  };

  // Проверяем, является ли пользователь администратором
  if (!user || !user.roles.includes('ADMIN')) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-screen bg-gray-200">
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <ShieldCheckIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Доступ запрещен</h2>
              <p className="text-gray-600">Эта страница доступна только администраторам.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-screen bg-gray-200">
      <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Настройки системы</h1>
            <p className="text-gray-600">Управление административными настройками</p>
          </div>

          {/* Табы */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('archive')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'archive'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <ArchiveBoxIcon className="h-5 w-5 inline mr-2" />
                  Архивация
                </button>
                <button
                  onClick={() => setActiveTab('telegram')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'telegram'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 inline mr-2" />
                  Telegram
                </button>
              </nav>
            </div>
          </div>

          {/* Контент табов */}
          {activeTab === 'archive' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-6">
                <Cog6ToothIcon className="h-6 w-6 text-gray-500 mr-3" />
                <h2 className="text-lg font-semibold text-gray-900">Настройки архивации</h2>
              </div>

              <div className="space-y-6">
                {/* Автоматическая архивация */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <ArchiveBoxIcon className="h-5 w-5 text-orange-500 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Автоматическая архивация задач</h3>
                      <p className="text-sm text-gray-500">
                        Если включено, все выполненные задачи будут автоматически архивироваться в конце каждого месяца.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {isLoadingSettings ? (
                      <div className="h-6 w-11 rounded-full bg-gray-200 animate-pulse"></div>
                    ) : projectSettings ? (
                      <button
                        onClick={handleAutoArchiveToggle}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                          projectSettings.autoArchiveEnabled ? 'bg-orange-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            projectSettings.autoArchiveEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    ) : (
                      <div className="text-red-500 text-sm">Ошибка загрузки настроек</div>
                    )}
                  </div>
                </div>

                {/* Информация о настройке */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Как это работает:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• При включенной настройке система автоматически архивирует выполненные задачи</li>
                    <li>• Архивация происходит в 23:00 последнего дня каждого месяца</li>
                    <li>• Архивируются только задачи, которые находятся в статусе "Выполнено"</li>
                    <li>• Задачи перемещаются в архив и удаляются из основного списка</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'telegram' && (
            <TelegramSettings />
          )}
        </div>
      </main>
    </div>
  );
};

export default Settings; 